/**
 * DenunciaForm - Formulario de denuncia
 * Features:
 * - Opción anónima o identificada
 * - Tipos de denuncia
 * - Descripción detallada
 * - Adjuntos (preparado)
 */
import { useState } from 'react';
import { AlertCircle, UserX, User, FileText } from 'lucide-react';
import { denunciasAPI } from '../../api';
import { Button, Alert } from '../common';
import useAuthStore from '../../store/authStore';

const DenunciaForm = ({ onSuccess, onCancel }) => {
  const { user } = useAuthStore();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Formulario
  const [form, setForm] = useState({
    tipo_denuncia: '',
    descripcion: '',
    anonima: true,
    prioridad: 'media',
  });

  const tiposDenuncia = [
    { value: 'acoso', label: 'Acoso' },
    { value: 'discriminacion', label: 'Discriminación' },
    { value: 'conflicto_interes', label: 'Conflicto de Interés' },
    { value: 'mal_uso_recursos', label: 'Mal Uso de Recursos' },
    { value: 'fraude', label: 'Fraude' },
    { value: 'violacion_codigo', label: 'Violación al Código de Ética' },
    { value: 'otro', label: 'Otro' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.tipo_denuncia) {
      setError('Debes seleccionar un tipo de denuncia');
      return;
    }

    if (!form.descripcion.trim()) {
      setError('La descripción es requerida');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const data = {
        tipo_denuncia: form.tipo_denuncia,
        descripcion: form.descripcion,
        anonima: form.anonima,
        prioridad: form.prioridad,
      };

      await denunciasAPI.create(data);
      
      setSuccess('Denuncia registrada correctamente');
      
      if (onSuccess) {
        setTimeout(() => onSuccess(), 1000);
      }
    } catch (err) {
      console.error('Error al crear denuncia:', err);
      setError(err.message || 'Error al crear denuncia');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Alertas */}
      {error && (
        <Alert
          type="error"
          title="Error"
          message={error}
          onClose={() => setError(null)}
        />
      )}

      {success && (
        <Alert
          type="success"
          title="Éxito"
          message={success}
          onClose={() => setSuccess(null)}
        />
      )}

      {/* Información importante */}
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
        <div className="flex items-start gap-2">
          <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-medium mb-1">Confidencialidad Garantizada</p>
            <p>
              Todas las denuncias son tratadas con estricta confidencialidad. 
              Si eliges hacer una denuncia anónima, tu identidad no será revelada bajo ninguna circunstancia.
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Tipo de denuncia (anónima o identificada) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Tipo de Denuncia *
          </label>
          <div className="grid grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => setForm({ ...form, anonima: true })}
              className={`p-4 border-2 rounded-lg transition-all ${
                form.anonima
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <div className="flex flex-col items-center gap-2">
                <UserX
                  className={`h-8 w-8 ${
                    form.anonima ? 'text-primary-600' : 'text-gray-400'
                  }`}
                />
                <span className="font-medium">Anónima</span>
                <span className="text-xs text-gray-600 text-center">
                  Tu identidad quedará protegida
                </span>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setForm({ ...form, anonima: false })}
              className={`p-4 border-2 rounded-lg transition-all ${
                !form.anonima
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <div className="flex flex-col items-center gap-2">
                <User
                  className={`h-8 w-8 ${
                    !form.anonima ? 'text-primary-600' : 'text-gray-400'
                  }`}
                />
                <span className="font-medium">Identificada</span>
                <span className="text-xs text-gray-600 text-center">
                  Permite seguimiento directo
                </span>
              </div>
            </button>
          </div>
        </div>

        {/* Categoría */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Categoría *
          </label>
          <select
            value={form.tipo_denuncia}
            onChange={(e) => setForm({ ...form, tipo_denuncia: e.target.value })}
            required
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">Selecciona una categoría</option>
            {tiposDenuncia.map((tipo) => (
              <option key={tipo.value} value={tipo.value}>
                {tipo.label}
              </option>
            ))}
          </select>
        </div>

        {/* Prioridad */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Prioridad
          </label>
          <select
            value={form.prioridad}
            onChange={(e) => setForm({ ...form, prioridad: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="baja">Baja</option>
            <option value="media">Media</option>
            <option value="alta">Alta</option>
          </select>
          <p className="text-sm text-gray-500 mt-1">
            Selecciona "Alta" solo para situaciones urgentes
          </p>
        </div>

        {/* Descripción */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Descripción Detallada *
          </label>
          <textarea
            value={form.descripcion}
            onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
            required
            rows={6}
            placeholder="Describe la situación con el mayor detalle posible: qué ocurrió, cuándo, dónde, quiénes estuvieron involucrados, etc."
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          <p className="text-sm text-gray-500 mt-1">
            {form.descripcion.length} / 2000 caracteres
          </p>
        </div>

        {/* Información adicional */}
        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-lg">
          <div className="flex items-start gap-2">
            <FileText className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-yellow-900">
              <p className="font-medium mb-1">¿Qué sucede después?</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Tu denuncia será revisada por el comité de ética</li>
                <li>Se asignará un investigador al caso</li>
                <li>Se realizará una investigación confidencial</li>
                <li>Recibirás actualizaciones sobre el progreso (si no es anónima)</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Botones */}
        <div className="flex gap-3 justify-end pt-4 border-t">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={loading}
            >
              Cancelar
            </Button>
          )}

          <Button
            type="submit"
            variant="primary"
            disabled={loading}
            loading={loading}
          >
            Enviar Denuncia
          </Button>
        </div>
      </form>
    </div>
  );
};

export default DenunciaForm;
